/**
 * Registration review — server state.
 *
 * Plan D8 allows React Query and no other store. The mutations here invalidate
 * their own keys rather than writing into a cache by hand: after an approval the
 * request is gone from the SUBMITTED queue and a volunteer exists that did not
 * before, and a hand-patched cache would have to know both of those things.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  approveRegistration,
  fetchAwaitingDecision,
  fetchRegistration,
  fetchRegistrations,
  rejectRegistration,
} from '../services/api/registrations';
import type { RegistrationQueueQuery } from '../services/api/registrations';

import { registrationKeys, volunteerKeys } from './queryKeys';

export { registrationKeys };

/**
 * The submissions panel at the top of the volunteers screen (Plan D12).
 *
 * `staleTime: 0` because this is the one list several people work at once, and a
 * cached copy showing someone who was activated a minute ago is how two staff
 * members end up deciding the same registration.
 */
export function useAwaitingDecision() {
  return useQuery({
    queryKey: registrationKeys.awaitingDecision,
    queryFn: fetchAwaitingDecision,
    staleTime: 0,
  });
}

export function useRegistrations(params: RegistrationQueueQuery) {
  return useQuery({
    queryKey: registrationKeys.list(params),
    queryFn: () => fetchRegistrations(params),
    // The queue is worked by several people at once, so a stale page is
    // misleading in a way a stale reference list is not.
    staleTime: 0,
  });
}

export function useRegistration(id: string | undefined) {
  return useQuery({
    queryKey: registrationKeys.detail(id ?? ''),
    queryFn: () => fetchRegistration(id as string),
    enabled: Boolean(id),
  });
}

export function useApproveRegistration(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => approveRegistration(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      // Approval created a volunteer. Any volunteer list already on screen is
      // now one row short of the truth.
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      toast.success('Registration approved.');
    },
    onError: (error) => toast.error(error, 'Could not approve the registration.'),
  });
}

export function useRejectRegistration(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => rejectRegistration(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      toast.success('Registration rejected.');
    },
    onError: (error) => toast.error(error, 'Could not reject the registration.'),
  });
}
